import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const RESEND_COOLDOWN_SECONDS = 60;

const VerifyEmailPage: React.FC = () => {
  const { user, loading, sendVerificationEmail, refreshUser, applyEmailVerificationCode, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['auth']);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [verifyingLink, setVerifyingLink] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handledActionCodeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { replace: true });
    }
  }, [loading, navigate, user]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => {
      setCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    const actionCode = params.get('oobCode');
    if (mode !== 'verifyEmail' || !actionCode || handledActionCodeRef.current === actionCode) {
      return;
    }
    handledActionCodeRef.current = actionCode;

    let active = true;
    const verifyEmailLink = async () => {
      setVerifyingLink(true);
      setErrorMessage(null);
      try {
        const refreshed = await applyEmailVerificationCode(actionCode);
        if (!active) return;
        if (refreshed?.emailVerified) {
          toast.success(t('verify.toastVerified'));
          navigate('/upload', { replace: true });
          return;
        }
        toast.info('Email verification link was applied. Refreshing account status may take a moment.');
      } catch (error: any) {
        if (!active) return;
        const message = error?.message || 'Failed to verify email from link.';
        setErrorMessage(message);
        toast.error(message);
      } finally {
        if (active) {
          setVerifyingLink(false);
        }
      }
    };

    void verifyEmailLink();

    return () => {
      active = false;
    };
  }, [applyEmailVerificationCode, location.search, navigate, t]);

  const handleResend = async () => {
    if (sending || cooldown > 0) return;
    setSending(true);
    setErrorMessage(null);
    try {
      const redirectUrl = `${window.location.origin}/verify-email`;
      await sendVerificationEmail(redirectUrl);
      toast.success(t('verify.toastSent'));
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error: any) {
      const message = error?.message || t('verify.toastError');
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const handleRefresh = async () => {
    if (refreshing || verifyingLink) return;
    setRefreshing(true);
    setErrorMessage(null);
    try {
      const refreshed = await refreshUser();
      if (refreshed?.emailVerified) {
        toast.success(t('verify.toastVerified'));
        navigate('/upload', { replace: true });
      } else {
        toast.info(t('verify.toastPending'));
      }
    } catch (error: any) {
      const message = error?.message || t('verify.toastError');
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error: any) {
      toast.error(error?.message || t('verify.toastError'));
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-xl w-full space-y-6 bg-white shadow-lg border border-gray-200 rounded-lg p-8">
        <div className="text-center space-y-2">
          <div className="text-3xl">📧</div>
          <h1 className="text-2xl font-bold text-gray-900">{t('verify.title')}</h1>
          <p className="text-sm text-gray-600">{t('verify.subtitle')}</p>
          <p className="text-sm text-gray-800 font-medium">{user.email}</p>
        </div>

        <div className="space-y-3 text-sm text-gray-600">
          <p>{t('verify.instructions')}</p>
          <p>{t('verify.checkSpam')}</p>
        </div>

        {errorMessage && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleResend}
            className="btn-primary flex-1"
            disabled={sending || cooldown > 0}
          >
            {cooldown > 0
              ? t('verify.resendCooldown', { seconds: cooldown })
              : t('verify.resend')}
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            className="btn-secondary flex-1"
            disabled={refreshing || verifyingLink}
          >
            {refreshing || verifyingLink ? t('verify.refreshing') : t('verify.refresh')}
          </button>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-600">
          <button type="button" onClick={handleLogout} className="text-primary-600 hover:text-primary-500">
            {t('verify.signOut')}
          </button>
          <span>{user.emailVerified ? t('verify.statusVerified') : t('verify.statusPending')}</span>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
